import { useState } from "react";
import {
  collection,
  doc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "@poseidon/firebase/firebase";
import configConstants from "@poseidon/utils/config";

const { SaltCollection } = configConstants;

// Builds the application document from a flat `getQuote` result, mirroring the
// fields salt-app's createApplication writes plus the rate/quote-meta fields so
// the application's Quote Details panel renders fully.
const buildApplicationData = (quote, sailorRef) => ({
  applicants: [sailorRef],
  costOfVessel: quote.costOfVessel,
  deposit: quote.deposit,
  termMonths: quote.termMonths,
  principal: quote.principal,
  monthlyPayment: quote.monthlyPayment,
  apr: quote.apr,
  annualRate: quote.annualRate,
  baseRate: quote.baseRate,
  addonRate: quote.addonRate,
  costOfCredit: quote.costOfCredit,
  totalPayable: quote.totalPayable,
  // only set for balloon applications; absent leaves the doc unchanged
  ...(quote.balloon > 0 ? { balloon: quote.balloon } : {}),
  quoteValid: true,
  quoteGeneratedAt: serverTimestamp(),
  status: "active",
  createdDate: serverTimestamp(),
});

// Builds the boat/vessel document linked to the application. Mirrors the boat
// create rule's allowed fields (application, make, model, type, createdAt); the
// backend `onBoatCreate` trigger fills in the remaining fields (e.g. hin).
const buildBoatData = (applicationRef, boat) => ({
  application: applicationRef,
  make: boat.make ?? "",
  model: boat.model ?? "",
  type: boat.type ?? "",
  createdAt: serverTimestamp(),
});

/**
 * Creates an application document (client-side, mirroring salt-app) linked to a
 * sailor via `applicants: [sailorRef]`. Admins are permitted to create on behalf
 * of any sailor by the `isAdmin()` branch added to the applications create rule.
 *
 * When boat details (make/model/type) are provided, a linked `boats/{id}` doc is
 * created in the same batch and wired bidirectionally: the application's `vessel`
 * points at the boat and the boat's `application` points back at the application.
 */
export const useCreateApplication = () => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(undefined);

  const createApplication = async ({ sailorId, quote, boat }) => {
    setSaving(true);
    setError(undefined);
    try {
      const sailorRef = doc(db, SaltCollection.Sailor, sailorId);
      const applicationRef = doc(collection(db, SaltCollection.Application));
      const applicationData = buildApplicationData(quote, sailorRef);

      const batch = writeBatch(db);
      const hasBoat = boat && (boat.make || boat.model || boat.type);
      if (hasBoat) {
        const boatRef = doc(collection(db, SaltCollection.Boat));
        // Link the application to its vessel before writing it.
        applicationData.vessel = boatRef;
        batch.set(boatRef, buildBoatData(applicationRef, boat));
      }
      batch.set(applicationRef, applicationData);
      await batch.commit();

      return applicationRef.id;
    } catch (err) {
      // Surface the real Firestore error (e.g. permission-denied vs
      // unavailable) instead of swallowing it.
      console.error("Failed to create application", err);
      const message = err?.code
        ? `${err.code}: ${err.message}`
        : err?.message ?? "Failed to create application";
      setError(message);
      throw new Error(message);
    } finally {
      setSaving(false);
    }
  };

  return { createApplication, saving, error };
};
